import React, { useState, useEffect, useRef } from 'react';
import api from '../../api/axiosConfig';

const AddressAutocomplete = ({ 
  value, 
  onChange, 
  type = 'pickup', // 'pickup' or 'dropoff'
  placeholder = 'Adres giriniz',
  className = '',
  required = false,
  error = false 
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Google Maps Autocomplete API çağrısı
  const fetchAddressSuggestions = async (searchTerm = '') => {
    if (!searchTerm || searchTerm.length < 6) {
      setSuggestions([]);
      return;
    }
    
    try {
      setLoading(true);
      console.log('🔍 Google Maps Autocomplete API çağrısı:', searchTerm);
      
      const response = await api.get('/api/maps/places/autocomplete', {
        params: {
          query: searchTerm,
          limit: 5
        }
      });
      
      if (response.data.status === 'OK' && response.data.suggestions) {
        console.log('✅ Autocomplete sonuç:', response.data.suggestions.length, 'öneri');
        setSuggestions(response.data.suggestions);
      } else {
        console.log('❌ Autocomplete sonuç yok');
        setSuggestions([]);
      }
      
    } catch (error) {
      console.error('❌ Autocomplete API hatası:', error);
      setSuggestions([]);
      
      // Hata durumunda fallback ana adresler
      const fallbackAddresses = [
        { description: 'BHT Clinic İstanbul Tema Hastanesi', main_text: 'BHT Clinic İstanbul Tema Hastanesi', secondary_text: 'Küçükçekmece/İstanbul' },
        { description: 'İstanbul Havalimanı', main_text: 'İstanbul Havalimanı', secondary_text: 'Arnavutköy/İstanbul' },
        { description: 'Sabiha Gökcen Havalimanı', main_text: 'Sabiha Gökcen Havalimanı', secondary_text: 'Pendik/İstanbul' }
      ].filter(addr => 
        addr.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      if (fallbackAddresses.length > 0) {
        setSuggestions(fallbackAddresses);
      }
    } finally {
      setLoading(false);
    }
  };

  // Ana adresleri göster (focus olduğunda veya boşken)
  const showMainAddresses = () => {
    const mainAddresses = [
      { 
        description: 'Sabiha Gökcen Havalimanı', 
        main_text: 'Sabiha Gökcen Havalimanı', 
        secondary_text: 'Pendik/İstanbul',
        place_id: 'main_sabiha'
      },
      { 
        description: 'İstanbul Havalimanı', 
        main_text: 'İstanbul Havalimanı', 
        secondary_text: 'Arnavutköy/İstanbul',
        place_id: 'main_istanbul_airport'
      },
      { 
        description: 'BHT Clinic İstanbul Tema Hastanesi', 
        main_text: 'BHT Clinic İstanbul Tema Hastanesi', 
        secondary_text: 'Küçükçekmece/İstanbul',
        place_id: 'main_bht'
      }
    ];
    
    setSuggestions(mainAddresses);
    console.log('📍 Ana adresler gösteriliyor');
  };

  // Input'a odaklandığında önerileri göster
  const handleFocus = () => {
    if (value && value.length >= 6) {
      // Zaten bir değer varsa API çağrısı yap
      fetchAddressSuggestions(value);
    } else {
      // Boşsa veya kısaysa ana adresleri göster
      showMainAddresses();
    }
    setShowSuggestions(true);
  };

  // Input değişikliği - Google Maps Autocomplete API ile (1 saniye debouncing)
  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    onChange(inputValue);
    
    // 1 saniye debouncing için timeout
    if (window.searchTimeout) {
      clearTimeout(window.searchTimeout);
    }
    
    window.searchTimeout = setTimeout(() => {
      if (inputValue.trim() === '') {
        // Boşsa ana adresleri göster
        showMainAddresses();
        setShowSuggestions(true);
        setLoading(false);
      } else if (inputValue.trim().length < 6) {
        // 6 karakterden az ise önerileri temizle
        setSuggestions([]);
        setShowSuggestions(false);
        setLoading(false);
      } else {
        // 6+ karakter varsa Google Maps Autocomplete API çağrısı
        fetchAddressSuggestions(inputValue.trim());
        setShowSuggestions(true);
      }
    }, 1000); // 1000ms = 1 saniye debouncing
  };

  // Öneri seçimi
  const handleSuggestionClick = (suggestion) => {
    // Google Places suggestion'dan formatted_address'i kullan
    const selectedAddress = suggestion.description || suggestion.formatted_address || suggestion;
    onChange(selectedAddress);
    setShowSuggestions(false);
  };

  // Dışarı tıklama ile önerileri gizle
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current && 
        !inputRef.current.contains(event.target) &&
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  // Backend'de filtreleme yapıldığı için burada filtreleme yapmıyoruz
  const filteredSuggestions = suggestions;

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        required={required}
        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 ${
          error ? 'border-red-500' : 'border-gray-200'
        } ${className}`}
      />
      
      {/* Suggestions Dropdown */}
      {showSuggestions && (filteredSuggestions.length > 0 || loading) && (
        <div
          ref={suggestionsRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto"
        >
          {loading ? (
            <div className="px-3 py-2 text-gray-500 text-sm flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500 mr-2"></div>
              Yükleniyor...
            </div>
          ) : (
            <>
              {filteredSuggestions.map((suggestion, index) => (
                <div
                  key={suggestion.place_id || index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-start">
                    <svg className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      {suggestion.main_text && suggestion.secondary_text ? (
                        // Google Places API formatı
                        <div>
                          <div className="font-medium text-gray-900 truncate">
                            {suggestion.main_text}
                          </div>
                          <div className="text-gray-500 text-xs truncate">
                            {suggestion.secondary_text}
                          </div>
                        </div>
                      ) : (
                        // Fallback - sadece description
                        <span className="truncate text-gray-900">
                          {suggestion.description || suggestion.formatted_address || suggestion}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {filteredSuggestions.length === 0 && !loading && value && value.length >= 2 && (
                <div className="px-3 py-2 text-gray-500 text-sm">
                  Bu arama için adres bulunamadı
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
